import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  CustomInput,
  Form,
  FormGroup,
  Input,
  Label,
  Row
} from 'reactstrap'
import bg1 from '../../../assets/img/generic/condominium.jpg'
import {
  DELETION_REASONS,
  DELETION_REASON_OTHER,
  submitAccountDeletionRequest
} from '../../../helpers/api/accountDeletion'
import { ERROR_RESPONSE, SUCCESS_RESPONSE } from '../../../helpers/codeResponses/codes'
import Section from '../../common/Section'
import Logo from '../../navbar/Logo'
import { PreloadState } from '../../UI/preloading/PreloadState'
import { Toast } from '../../UI/toast/Toast'

const AccountDeletion = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [dni, setDni] = useState('')
  const [condominiumName, setCondominiumName] = useState('')
  const [reason, setReason] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [confirm, setConfirm] = useState(false)

  const handleSubmit = (event) => {
    event.preventDefault()

    setIsLoading(true)

    submitAccountDeletionRequest({
      full_name: fullName,
      email,
      phone: phone || null,
      dni: dni || null,
      condominium_name: condominiumName || null,
      reason: reason === DELETION_REASON_OTHER ? reasonDetail.trim() : reason,
      source: 'web',
      confirm: true
    })
      .then(({ code }) => {
        if (code === SUCCESS_RESPONSE.CREATED) {
          setSent(true)
        }
      })
      .catch((error) => {
        const data = error?.data || error
        // El backend responde 409 cuando ya hay una solicitud en curso para
        // ese correo: no es un error del formulario, hay que explicarlo.
        if (data?.code === ERROR_RESPONSE.PENDING_ALREADY_EXIST) {
          Toast.fire({
            type: 'error',
            title: 'Ya existe una solicitud en curso para este correo electrónico.'
          })
          return
        }

        const fieldErrors = data?.errors && Object.values(data.errors).flat()
        Toast.fire({
          type: 'error',
          title: fieldErrors?.length
            ? fieldErrors[0]
            : 'No pudimos registrar tu solicitud, por favor reintenta nuevamente.'
        })
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <Section
      id="account-deletion"
      className="py-0 pt-4"
      image={bg1}
      style={{ backgroundSize: 'cover' }}
      position="center bottom"
      overlay
    >
      <Row className="flex-center min-vh-100 py-6">
        <Col>
          <Row className="justify-content-center">
            <Col sm={12} className="text-center mb-4">
              <h2 className="col-12 text-center text-white">Eliminación de cuenta e información</h2>
              <p className="col-12 text-center text-white fs--1 mb-0">
                Solicita la eliminación de tu cuenta de Homelan y de los datos asociados a ella.
              </p>
            </Col>
            <Col sm={12} md={10} lg={7}>
              <Card>
                <Logo to={'/'} classImage="mb-0 mt-4" />
                <CardBody className="fs--1 font-weight-normal p-5 pt-3">
                  {sent ? (
                    <div className="text-center">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-success mb-3"
                        style={{ fontSize: '3rem' }}
                      />
                      <h4>Revisa tu correo</h4>
                      <p className="mb-3">
                        Enviamos un enlace de confirmación a <b>{email}</b>. Debes abrirlo para que
                        podamos procesar tu solicitud. El enlace vence en 24 horas.
                      </p>
                      <p className="text-600 mb-4">
                        Si no lo recibes en unos minutos, revisa tu carpeta de spam o correo no
                        deseado.
                      </p>
                      <Button color="falcon-default" tag={Link} to="/">
                        Volver al inicio
                      </Button>
                    </div>
                  ) : (
                    <PreloadState loading={isLoading} message="Un momento, enviando tu solicitud">
                      <>
                        <Alert color="info" className="fs--1">
                          <p className="mb-2">
                            <b>¿Cómo funciona?</b>
                          </p>
                          <ol className="mb-2 pl-3">
                            <li>Completa este formulario con los datos de tu cuenta.</li>
                            <li>Confirma la solicitud desde el enlace que enviaremos a tu correo.</li>
                            <li>
                              Nuestro equipo la procesa en un plazo máximo de 30 días hábiles y te
                              notifica al mismo correo.
                            </li>
                          </ol>
                          <p className="mb-0">
                            Al eliminar tu cuenta se borran tus datos personales, tu perfil y tu
                            historial de accesos. Cierta información asociada a pagos y estados de
                            cuenta puede conservarse el tiempo que exija la normativa contable
                            aplicable. Puedes revisar el detalle en nuestra{' '}
                            <Link to="/privacy">política de privacidad</Link>.
                          </p>
                        </Alert>

                        <Form onSubmit={handleSubmit} className="text-left justify-content-between">
                          <Row>
                            <FormGroup tag={Col} sm={12}>
                              <Label>NOMBRES Y APELLIDOS *</Label>
                              <Input
                                required
                                value={fullName}
                                placeholder={'Nombres y apellidos'}
                                onChange={({ target }) => setFullName(target.value)}
                              />
                            </FormGroup>
                          </Row>
                          <Row>
                            <FormGroup tag={Col} sm={6}>
                              <Label>CORREO DE LA CUENTA *</Label>
                              <Input
                                required
                                type="email"
                                value={email}
                                placeholder={'Correo electrónico'}
                                onChange={({ target }) => setEmail(target.value)}
                              />
                            </FormGroup>
                            <FormGroup tag={Col} sm={6}>
                              <Label>TELÉFONO</Label>
                              <Input
                                value={phone}
                                placeholder={'Teléfono'}
                                onChange={({ target }) => setPhone(target.value)}
                              />
                            </FormGroup>
                          </Row>
                          <Row>
                            <FormGroup tag={Col} sm={6}>
                              <Label>DOCUMENTO DE IDENTIDAD</Label>
                              <Input
                                value={dni}
                                placeholder={'Cédula o documento'}
                                onChange={({ target }) => setDni(target.value)}
                              />
                            </FormGroup>
                            <FormGroup tag={Col} sm={6}>
                              <Label>CONDOMINIO</Label>
                              <Input
                                value={condominiumName}
                                placeholder={'Nombre del condominio'}
                                onChange={({ target }) => setCondominiumName(target.value)}
                              />
                            </FormGroup>
                          </Row>
                          <Row>
                            <FormGroup tag={Col} sm={12}>
                              <Label>MOTIVO *</Label>
                              <Input
                                required
                                type="select"
                                value={reason}
                                onChange={({ target }) => setReason(target.value)}
                              >
                                <option value="">Selecciona un motivo...</option>
                                {DELETION_REASONS.map((label) => (
                                  <option key={label} value={label}>
                                    {label}
                                  </option>
                                ))}
                                <option value={DELETION_REASON_OTHER}>
                                  {DELETION_REASON_OTHER}
                                </option>
                              </Input>
                            </FormGroup>
                          </Row>
                          {reason === DELETION_REASON_OTHER && (
                            <Row>
                              <FormGroup tag={Col} sm={12}>
                                <Label>¿CUÁL ES EL MOTIVO? *</Label>
                                <Input
                                  required
                                  type="textarea"
                                  rows={2}
                                  value={reasonDetail}
                                  placeholder={'Cuéntanos por qué deseas eliminarla'}
                                  onChange={({ target }) => setReasonDetail(target.value)}
                                />
                              </FormGroup>
                            </Row>
                          )}
                          <FormGroup>
                            <CustomInput
                              type="checkbox"
                              id="confirm-account-deletion"
                              checked={confirm}
                              onChange={({ target }) => setConfirm(target.checked)}
                              label="Entiendo que esta acción es irreversible y que perderé el acceso a mi cuenta y a la información asociada."
                            />
                          </FormGroup>
                          <FormGroup>
                            <Button
                              type="submit"
                              color="danger"
                              block
                              className="mt-3"
                              disabled={
                              !confirm ||
                              !reason ||
                              (reason === DELETION_REASON_OTHER && !reasonDetail.trim())
                            }
                            >
                              Enviar solicitud
                            </Button>
                          </FormGroup>
                        </Form>
                      </>
                    </PreloadState>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Section>
  )
}

export default AccountDeletion
